/**
 * E6 (QUALITY-NCR-REVIEW, Aaron 29 Sep 2026): when the containment and the
 * corrective action are due, beside each on the 8D tabs, as legacy showed the
 * supplier. Required dates are calculated by the server from the customer's
 * working-day lead times (the grid's own `ca_required_date` /
 * `car_required_date`); never set per NCR.
 */
import { useFormatters } from '../../contexts/PreferencesContext';
import { daysWaitingText } from '../../components/quality/ncrPlate';
import { todayLocalISO } from './localDate';
import type { NCREditFormData } from './types';

type Step = 'containment' | 'corrective_action';

const FIELDS: Record<Step, { title: string; required: string; submitted: string }> = {
  containment: { title: 'Containment', required: 'ca_required_date', submitted: 'ca_submitted_date' },
  corrective_action: { title: 'Corrective action', required: 'car_required_date', submitted: 'car_submitted_date' },
};

interface LeadTimeTrackingProps {
  step: Step;
  formData: NCREditFormData;
}

/**
 * Required by / Submitted / Days waiting for one 8D step. Renders nothing on
 * a draft, when the NCR has no 8D, or until the server has a required date.
 *
 * @param step - which 8D step's dates to show
 * @param formData - the NCR as loaded (carries the dates and `days_waiting`)
 */
export function LeadTimeTracking({ step, formData }: LeadTimeTrackingProps) {
  const { formatDate } = useFormatters();
  const f = FIELDS[step];
  const required = formData[f.required] as string | null | undefined;
  const submitted = formData[f.submitted] as string | null | undefined;
  // Round-1 review: the server computes required dates for every NCR, so a
  // draft or an NCR with no 8D would otherwise show deadlines nobody owes.
  if (!required || formData.status === 'draft' || formData.is_8d_required === false) return null;
  const waiting = formData.days_waiting?.[step];
  const overdue = !submitted && required < todayLocalISO();

  return (
    <dl
      className="grid grid-cols-3 gap-4 rounded-lg border bg-muted/40 px-4 py-3 text-sm"
      aria-label={`${f.title} lead time`}
      data-testid={`lead-time-${step}`}
    >
      <div>
        <dt className="text-xs text-muted-foreground">{f.title} required by</dt>
        <dd className={overdue ? 'font-medium text-red-600' : 'font-medium text-foreground'}>
          {formatDate(required)}{overdue && ' (overdue)'}
        </dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Submitted</dt>
        <dd className="font-medium text-foreground">{submitted ? formatDate(submitted) : 'Not yet'}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Days waiting</dt>
        <dd className="font-medium tabular-nums text-foreground">{daysWaitingText(waiting) ?? '-'}</dd>
      </div>
    </dl>
  );
}
